import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Phone, MessageCircle, Search, Save, PhoneOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusSelect } from "@/components/StatusSelect";
import { PaymentDialog } from "@/components/PaymentDialog";
import { admissionDue, admissionWarn, partialCfg, partsOf } from "@/lib/spot";
import { api } from "@/lib/api";
import { STATUSES, CLOSED, counselorOf } from "@/lib/status";
import { NotInterestedDialog, hasPayments } from "@/components/NotInterested";
import { waNumber } from "@/lib/pdf";
import { SpotCountdown } from "@/components/SpotCountdown";

const TABS = [{ key: "pending", label: "To call back" }, ...STATUSES, { key: "all", label: "All" }];
const today = () => new Date().toLocaleDateString("en-CA");

const DueTag = ({ r }) => {
  if (r.status === "admitted" || r.status === "not_interested" || !r.follow_up_date) return null;
  const t = today();
  if (r.follow_up_date < t) return <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white" data-testid={`fu-overdue-${r.receipt_no}`}>Overdue</span>;
  if (r.follow_up_date === t) return <span className="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white" data-testid={`fu-today-${r.receipt_no}`}>Today</span>;
  return null;
};

const Row = ({ r }) => {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [f, setF] = useState({ status: r.status || "enquiry", follow_up_date: r.follow_up_date || "", follow_up_note: r.follow_up_note || "" });
  const dirty = f.status !== (r.status || "enquiry") || f.follow_up_date !== (r.follow_up_date || "") || f.follow_up_note !== (r.follow_up_note || "");
  const [payOpen, setPayOpen] = useState(false);
  const [partOpen, setPartOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const save = async (payment, extra) => {
    if (f.status === "not_interested" && r.status !== "not_interested" && !extra) {
      return hasPayments(r) ? toast.error("Payments are recorded on this receipt - remove them before marking Not Interested") : setLostOpen(true);
    }
    if (f.status === "admitted" && r.status !== "admitted" && !payment) return setPayOpen(true);
    if (f.status === "partial" && r.status !== "partial" && !extra) return setPartOpen(true);
    try {
      await api.patch(`/receipts/${r.id}/status`, { ...f, ...(extra?.partial_payment !== undefined || extra?.only_form_fee ? { status: "partial" } : {}), payment, ...(extra || {}) });
      setPartOpen(false);
      setLostOpen(false);
      qc.invalidateQueries({ queryKey: ["receipts"] });
      qc.invalidateQueries({ queryKey: ["receipt", r.id] });
      toast.success(`${r.receipt_no} updated`);
    } catch (e) { toast.error(e?.response?.data?.detail?.toString?.() || "Could not update"); }
  };
  const i = r.inputs;
  return (
    <div className="grid gap-4 border-b border-slate-100 px-5 py-4 lg:grid-cols-[1.3fr_1.2fr_auto_1.6fr_auto] lg:items-center" data-testid={`fu-row-${r.receipt_no}`}>
      <div>
        <button onClick={() => nav(`/receipts/${r.id}`)} className="font-medium text-plum-950 hover:underline" data-testid={`fu-open-${r.receipt_no}`}>{i.childName}</button>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="font-num">{r.receipt_no}</span><span>{i.grade}</span><span>· {counselorOf(r)}</span><DueTag r={r} /><SpotCountdown r={r} testid={`fu-spot-countdown-${r.receipt_no}`} remind />
        </div>
      </div>
      <div className="text-sm">
        <div className="text-slate-700">{i.parentName || "—"}</div>
        {i.contact ? (
          <div className="mt-1 flex items-center gap-3 text-xs">
            <a href={`tel:${i.contact}`} className="flex items-center gap-1 text-slate-700 hover:text-plum-950" data-testid={`fu-call-${r.receipt_no}`}><Phone className="h-3.5 w-3.5" />{i.contact}</a>
            <a href={`https://wa.me/${waNumber(i.contact)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-green-700" data-testid={`fu-wa-${r.receipt_no}`}><MessageCircle className="h-3.5 w-3.5" />WhatsApp</a>
          </div>
        ) : <div className="mt-1 flex items-center gap-1 text-xs text-slate-400"><PhoneOff className="h-3.5 w-3.5" />No contact</div>}
        {r.status === "partial" && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-orange-900" data-testid={`fu-partial-${r.receipt_no}`}>
            <span>Paid ₹ {(partsOf(r).reduce((t, x) => t + Number(x.amount || 0), 0) + Number(r.form_fee?.amount || 0)).toLocaleString("en-IN")}{r.form_fee ? " (incl. form fee)" : ""} · Balance ₹ {Number(admissionDue(r) || 0).toLocaleString("en-IN")}</span>
            <button onClick={() => setPartOpen(true)} className="rounded-full border border-orange-300 bg-orange-50 px-2 py-0.5 font-semibold hover:bg-orange-100" data-testid={`fu-add-part-${r.receipt_no}`}>+ Add part-payment</button>
          </div>
        )}
        {r.status === "not_interested" && (
          <div className="mt-1.5 text-xs text-slate-500" data-testid={`fu-lost-reason-${r.receipt_no}`}><b>Not interested:</b> {r.lost_reason || "—"}</div>
        )}
      </div>
      <StatusSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} testid={`fu-status-${r.receipt_no}`} />
      <div className="grid grid-cols-[140px_1fr] gap-2">
        <Input type="date" value={f.follow_up_date} onChange={(e) => setF({ ...f, follow_up_date: e.target.value })} className="h-8 text-xs" data-testid={`fu-date-${r.receipt_no}`} />
        <Input placeholder="Call note…" value={f.follow_up_note} onChange={(e) => setF({ ...f, follow_up_note: e.target.value })} className="h-8 text-xs" data-testid={`fu-note-${r.receipt_no}`} />
      </div>
      <Button size="sm" disabled={!dirty} onClick={() => save()} className="h-8 bg-plum-700 hover:bg-plum-800" data-testid={`fu-save-${r.receipt_no}`}><Save className="mr-1.5 h-3.5 w-3.5" />Save</Button>
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} receiptNo={r.receipt_no} defaultAmount={admissionDue(r)} onConfirm={(p) => save(p)}
        warnFor={admissionWarn(r)} requireNote editHref={`/?edit=${r.id}`} />
      <PaymentDialog open={partOpen} onOpenChange={setPartOpen} receiptNo={r.receipt_no} defaultAmount=""
        partial={{ ...partialCfg(r), balanceDate: f.follow_up_date }} title="Partially paid" confirmLabel="Save part-payment"
        onConfirm={({ payment, only_form_fee, balance_date }) => save(null, { partial_payment: payment, only_form_fee, follow_up_date: balance_date })} />
      <NotInterestedDialog open={lostOpen} onOpenChange={setLostOpen} receiptNo={r.receipt_no}
        onConfirm={(reason) => save(null, { status: "not_interested", lost_reason: reason, follow_up_date: "" })} />
    </div>
  );
};

const Kpi = ({ label, value, testid, cls = "text-plum-950" }) => (
  <div className="rounded-lg border border-plum-900/10 bg-white px-5 py-4">
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`font-num mt-1.5 text-2xl font-semibold ${cls}`} data-testid={testid}>{value}</div>
  </div>
);

export default function FollowUps() {
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [who, setWho] = useState("all");
  const { data = [] } = useQuery({ queryKey: ["receipts"], queryFn: () => api.get("/receipts").then((r) => r.data) });
  const t = today();
  const pending = data.filter((r) => !CLOSED.includes(r.status));
  const counsellors = [...new Map([...data].reverse().map((r) => [counselorOf(r).toLowerCase(), counselorOf(r)])).values()].sort();

  const rows = useMemo(() => {
    const s = q.toLowerCase();
    return data
      .filter((r) => (tab === "all" ? true : tab === "pending" ? !CLOSED.includes(r.status) : (r.status || "enquiry") === tab))
      .filter((r) => who === "all" || counselorOf(r).toLowerCase() === who.toLowerCase())
      .filter((r) => [r.receipt_no, r.inputs.childName, r.inputs.parentName, r.inputs.contact].some((x) => (x || "").toLowerCase().includes(s)))
      .sort((a, b) => (a.follow_up_date || "9999").localeCompare(b.follow_up_date || "9999") || b.created_at.localeCompare(a.created_at));
  }, [data, tab, q, who]);

  return (
    <div>
      <div className="fade-up mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-plum-700">Admission pipeline</div>
        <h1 className="font-display mt-0.5 text-3xl font-bold leading-tight tracking-tight text-plum-950 sm:text-4xl">Follow-ups</h1>
      </div>

      <div className="fade-up d1 mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="To call back" value={pending.length} testid="fu-kpi-pending" />
        <Kpi label="Overdue" value={pending.filter((r) => r.follow_up_date && r.follow_up_date < t).length} testid="fu-kpi-overdue" cls="text-red-600" />
        <Kpi label="Due today" value={pending.filter((r) => r.follow_up_date === t).length} testid="fu-kpi-today" cls="text-amber-600" />
        <Kpi label="Admitted" value={`${data.filter((r) => r.status === "admitted").length} · ${data.length ? Math.round((data.filter((r) => r.status === "admitted").length / data.length) * 100) : 0}%`} testid="fu-kpi-admitted" cls="text-plum-700" />
      </div>

      <div className="fade-up d2 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1 rounded-lg border border-plum-900/10 bg-white p-1">
          {TABS.map((x) => (
            <button key={x.key} onClick={() => setTab(x.key)} data-testid={`fu-tab-${x.key}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${tab === x.key ? "bg-plum-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{x.label}</button>
          ))}
        </div>
        <Select value={who} onValueChange={setWho}>
          <SelectTrigger className="w-48 bg-white" data-testid="fu-counsellor-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All counsellors</SelectItem>
            {counsellors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative w-full sm:ml-auto sm:w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search child, parent, contact" className="bg-white pl-9" data-testid="fu-search-input" />
        </div>
      </div>

      <div className="fade-up d3 rounded-lg border border-plum-900/10 bg-white" data-testid="fu-list">
        {rows.map((r) => <Row key={`${r.id}-${r.status}-${r.follow_up_date}-${r.follow_up_note}`} r={r} />)}
        {!rows.length && <p className="px-5 py-12 text-center text-sm text-slate-500" data-testid="fu-empty">Nothing here — all caught up.</p>}
      </div>
    </div>
  );
}
